/**
 * Retail YaaS account enrollment against the account-only public contract.
 *
 * Wraps the two yaas-api endpoints:
 *   POST /api/yaas/wallet/:address/enroll/challenge — mint a payload-bound nonce.
 *   POST /api/yaas/wallet/:address/enroll          — submit the signed proof.
 *
 * The flow hook (`useYaasRetailFlow.submitApplication`) drives the
 * sequence: requestChallenge → useWalletVerification(purpose:"retail_enrollment") →
 * submitEnrollment.
 *
 * Both methods throw on non-OK responses.
 * On success, the account is adapted into the wallet cache before a
 * background dashboard reload starts.
 */

import type { ApplicationDraft } from "@shared/widgets/yaas/types";
import {
    API_URLS,
    ParticipantAccountStatus,
    type RetailEnrollmentAccount,
    type RetailEnrollmentErrorCode,
    type RetailEnrollmentPayload,
    type RetailEnrollmentResponse,
    type RetailWalletChallengeResponse,
    useSessionStore,
    type YaasWalletResponse
} from "@solomon-apps/shared";
import { useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { useState } from "react";

import type { WalletVerificationErrorCode } from "@/services/yaas/walletVerificationError";

import { yaasWalletQueryKey } from "./queryKeys";
import { assertRetailSquadsAvailable } from "./retailSquadsPreflight";
import type { VerificationProof } from "./useWalletVerification";
import { submitWithVerificationRetry } from "./verificationRequests";
import { isRecord, isWalletChallenge, readResponseJson } from "./verificationResponse";

export interface UseRetailEnrollmentResult {
    requestChallenge: (
        address: string,
        payload: ApplicationDraft,
        signal?: AbortSignal,
        onRequested?: () => void
    ) => Promise<RetailWalletChallengeResponse>;
    submitEnrollment: (
        address: string,
        payload: ApplicationDraft,
        nonce: string,
        proof: VerificationProof,
        shouldCommit: () => boolean,
        challengeExpiresAt: string,
        signal?: AbortSignal,
        onSubmitted?: () => void
    ) => Promise<RetailEnrollmentResponse>;
    isLoading: boolean;
    error: string | null;
}

export type RetailEnrollmentLocalErrorCode = "network_error" | "malformed_response" | WalletVerificationErrorCode;

export class RetailEnrollmentRequestError extends Error {
    constructor(
        message: string,
        public readonly code: RetailEnrollmentErrorCode | RetailEnrollmentLocalErrorCode,
        public readonly status: number | null,
        public readonly requestId: string | null = null,
        public readonly retryAfterSeconds: number | null = null
    ) {
        super(message);
        this.name = "RetailEnrollmentRequestError";
    }
}

/**
 * Body shape posted to /enroll/challenge. Exported so unit tests can
 * pin the on-the-wire field set without exercising React state.
 */
export function buildChallengeBody(payload: ApplicationDraft, signerAddress?: string): RetailEnrollmentPayload {
    if (payload.tosAccepted !== true) {
        throw new RetailEnrollmentRequestError(
            "Accept the Solomon Terms of Service before continuing",
            "validation_failed",
            null
        );
    }
    if (!payload.terms)
        throw new RetailEnrollmentRequestError(
            "Load and accept the published Terms before continuing",
            "terms_revision_mismatch",
            null
        );
    return {
        ...(signerAddress ? { signerAddress } : {}),
        distributionMode: payload.distributionMode,
        rewardsDestination: payload.rewardsDestination,
        tosAccepted: payload.tosAccepted,
        termsVersion: payload.terms.version,
        termsContentDigest: payload.terms.contentDigest,
        ...(payload.accountLabel?.trim() ? { accountLabel: payload.accountLabel.trim() } : {})
    };
}

/**
 * Body shape posted to /enroll. Mirrors `buildChallengeBody` and adds
 * the envelope fields (`nonce`, `proof`) the backend strips before
 * canonicalizing.
 */
export function buildSubmitBody(
    payload: ApplicationDraft,
    nonce: string,
    proof: VerificationProof,
    signerAddress?: string
): RetailEnrollmentPayload & { nonce: string; proof: VerificationProof } {
    return {
        nonce,
        ...buildChallengeBody(payload, signerAddress),
        proof
    };
}

/** Path for POST /enroll/challenge. Exported so the URL is testable. */
export function challengeUrl(apiBase: string, address: string): string {
    return `${apiBase}/api/yaas/wallet/${address}/enroll/challenge`;
}

/** Path for POST /enroll. Exported so the URL is testable. */
export function submitUrl(apiBase: string, address: string): string {
    return `${apiBase}/api/yaas/wallet/${address}/enroll`;
}

/** React Query cache key for a wallet's dashboard payload. */
export function adaptEnrollmentAccountToWalletCache(
    address: string,
    account: RetailEnrollmentAccount,
    existing: YaasWalletResponse | null | undefined
): YaasWalletResponse {
    const sameWallet = existing?.walletAddress === address && account.address === address;
    const programPositions = sameWallet ? existing.programPositions : [];
    return {
        walletAddress: address,
        account,
        programPositions,
        holdings: sameWallet ? existing.holdings : [],
        observation: sameWallet
            ? existing.observation
            : {
                  status: "unavailable",
                  source: "jachin",
                  ownerAddress: null,
                  complete: false,
                  lpComplete: false,
                  reason: "discovery_unavailable",
                  fetchedAt: null,
                  configVersion: null,
                  poolAddresses: [],
                  assetMints: []
              }
    };
}

const ERROR_CODES = [
    "validation_failed",
    "payload_mismatch",
    "signature_invalid",
    "verification_invalid",
    "verification_pending",
    "terms_revision_mismatch",
    "nonce_expired",
    "nonce_consumed",
    "account_pending",
    "account_suspended",
    "account_blacklisted",
    "account_closed",
    "rate_limited",
    "internal_error",
    "enrollment_unavailable"
] as const satisfies readonly RetailEnrollmentErrorCode[];

function isEnrollmentErrorCode(value: unknown): value is RetailEnrollmentErrorCode {
    return ERROR_CODES.some((code) => code === value);
}

function parseRetryAfter(value: string | null): number | null {
    if (value === null) return null;
    const seconds = Number(value);
    if (Number.isFinite(seconds) && seconds >= 0) return seconds;
    const retryAt = Date.parse(value);
    if (!Number.isFinite(retryAt)) return null;
    return Math.max(0, Math.ceil((retryAt - Date.now()) / 1000));
}

export async function readEnrollmentError(response: Response, fallback: string): Promise<RetailEnrollmentRequestError> {
    const body = await readResponseJson(response);
    const error = isRecord(body) ? body.error : null;
    if (
        !isRecord(error) ||
        !isEnrollmentErrorCode(error.code) ||
        typeof error.message !== "string" ||
        typeof error.requestId !== "string"
    ) {
        return new RetailEnrollmentRequestError(fallback, "malformed_response", response.status);
    }
    return new RetailEnrollmentRequestError(
        error.message,
        error.code,
        response.status,
        error.requestId,
        parseRetryAfter(response.headers.get("Retry-After"))
    );
}

export function parseEnrollmentSuccess(
    status: number,
    body: unknown,
    expectedAddress?: string
): RetailEnrollmentResponse {
    const result = isRecord(body) ? body : null;
    const expectedCreated = status === 201 ? true : status === 200 ? false : null;
    const account = isRecord(result?.account) ? result.account : null;
    const accountKeys = account && typeof account === "object" ? Object.keys(account).sort() : [];
    const expectedAccountKeys = [
        "address",
        "createdAt",
        "distributionMode",
        "isRewardsEligible",
        "rewardsDestinationAddress",
        "status",
        "totalEarnedRewards",
        "totalPositionsValue",
        "updatedAt",
        "uuid"
    ];
    if (account && Object.hasOwn(account, "label")) {
        expectedAccountKeys.push("label");
        expectedAccountKeys.sort();
    }
    if (account && expectedAddress !== undefined && account.address !== expectedAddress) {
        throw new RetailEnrollmentRequestError(
            "The enrollment response was for a different wallet",
            "wallet_mismatch",
            status
        );
    }
    if (
        expectedCreated === null ||
        result?.created !== expectedCreated ||
        Object.keys(result ?? {})
            .sort()
            .join(",") !== "account,created" ||
        !account ||
        accountKeys.join(",") !== expectedAccountKeys.join(",") ||
        account.status !== ParticipantAccountStatus.Active ||
        typeof account.uuid !== "string" ||
        typeof account.address !== "string" ||
        (account.label !== undefined && account.label !== null && typeof account.label !== "string") ||
        (account.rewardsDestinationAddress !== null && typeof account.rewardsDestinationAddress !== "string") ||
        (account.distributionMode !== null &&
            account.distributionMode !== "stream" &&
            account.distributionMode !== "claim") ||
        typeof account.isRewardsEligible !== "boolean" ||
        typeof account.totalPositionsValue !== "string" ||
        typeof account.totalEarnedRewards !== "string" ||
        typeof account.createdAt !== "string" ||
        typeof account.updatedAt !== "string"
    ) {
        throw new RetailEnrollmentRequestError("The enrollment response was invalid", "malformed_response", status);
    }
    return {
        created: expectedCreated,
        account: {
            uuid: account.uuid,
            address: account.address,
            ...(account.label === undefined ? {} : { label: account.label }),
            rewardsDestinationAddress: account.rewardsDestinationAddress,
            status: account.status,
            distributionMode: account.distributionMode,
            isRewardsEligible: account.isRewardsEligible,
            totalPositionsValue: account.totalPositionsValue,
            totalEarnedRewards: account.totalEarnedRewards,
            createdAt: account.createdAt,
            updatedAt: account.updatedAt
        }
    };
}

export async function readEnrollmentSuccess(
    response: Response,
    expectedAddress: string
): Promise<RetailEnrollmentResponse> {
    const body = await readResponseJson(response);
    return parseEnrollmentSuccess(response.status, body, expectedAddress);
}

export function commitEnrollmentSuccess(
    queryClient: Pick<QueryClient, "setQueryData" | "invalidateQueries">,
    setYaasStatus: (status: ParticipantAccountStatus) => void,
    address: string,
    account: RetailEnrollmentAccount,
    shouldCommit: () => boolean = () => true
): void {
    if (!shouldCommit()) return;
    const queryKey = yaasWalletQueryKey(address);
    queryClient.setQueryData<YaasWalletResponse | null>(queryKey, (existing) =>
        adaptEnrollmentAccountToWalletCache(address, account, existing)
    );
    setYaasStatus(ParticipantAccountStatus.Active);
    void queryClient.invalidateQueries({ queryKey, exact: true });
}

/** Only transport failures receive the network_error classification. */
async function requestEnrollmentResponse(request: () => Promise<Response>): Promise<Response> {
    try {
        return await request();
    } catch (error) {
        if (isRecord(error) && error.name === "AbortError") throw error;
        throw new RetailEnrollmentRequestError(
            error instanceof Error ? error.message : "The enrollment request failed",
            "network_error",
            null
        );
    }
}

/** A blank destination defaults to the account address on the API, which is unsafe for Squads. */
function assertSafeSquadsRewardsDestination(address: string, payload: ApplicationDraft, signerAddress?: string): void {
    if (!signerAddress) return;
    const destination = payload.rewardsDestination.trim();
    if (!destination || destination === address.trim()) {
        throw new RetailEnrollmentRequestError(
            "Use the Squads vault as the rewards destination, not the multisig account address.",
            "validation_failed",
            null
        );
    }
}

export const useRetailEnrollment = (signerAddress?: string): UseRetailEnrollmentResult => {
    const queryClient = useQueryClient();
    const setYaasStatus = useSessionStore((state) => state.setYaasStatus);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const requestChallenge = async (
        address: string,
        payload: ApplicationDraft,
        signal?: AbortSignal,
        onRequested?: () => void
    ): Promise<RetailWalletChallengeResponse> => {
        setIsLoading(true);
        setError(null);
        try {
            assertSafeSquadsRewardsDestination(address, payload, signerAddress);
            if (!signerAddress)
                await assertRetailSquadsAvailable(payload.rewardsDestination.trim() || address, address, signal);
            const requestBody = JSON.stringify(buildChallengeBody(payload, signerAddress));
            try {
                onRequested?.();
            } catch {
                /* Analytics is optional. */
            }
            const response = await requestEnrollmentResponse(() =>
                fetch(challengeUrl(API_URLS.YAAS, address), {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: requestBody,
                    signal
                })
            );
            if (!response.ok) {
                const requestError = await readEnrollmentError(response, "Failed to request enrollment challenge");
                throw requestError;
            }
            const body = await readResponseJson(response);
            if (!isWalletChallenge(body)) {
                const requestError = new RetailEnrollmentRequestError(
                    "The enrollment challenge was invalid",
                    "malformed_response",
                    response.status
                );
                throw requestError;
            }
            return body;
        } catch (error) {
            if (error instanceof Error) setError(error.message);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const submitEnrollment = async (
        address: string,
        payload: ApplicationDraft,
        nonce: string,
        proof: VerificationProof,
        shouldCommit: () => boolean,
        challengeExpiresAt: string,
        signal?: AbortSignal,
        onSubmitted?: () => void
    ): Promise<RetailEnrollmentResponse> => {
        setIsLoading(true);
        setError(null);
        try {
            assertSafeSquadsRewardsDestination(address, payload, signerAddress);
            if (!signerAddress)
                await assertRetailSquadsAvailable(payload.rewardsDestination.trim() || address, address, signal);
            const requestBody = JSON.stringify(buildSubmitBody(payload, nonce, proof, signerAddress));
            try {
                onSubmitted?.();
            } catch {
                /* Analytics is optional. */
            }
            const response = await requestEnrollmentResponse(() =>
                submitWithVerificationRetry(
                    submitUrl(API_URLS.YAAS, address),
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        signal,
                        body: requestBody
                    },
                    challengeExpiresAt
                )
            );
            if (!response.ok) {
                const requestError = await readEnrollmentError(response, "Failed to submit enrollment");
                throw requestError;
            }
            const data = await readEnrollmentSuccess(response, address);
            commitEnrollmentSuccess(
                queryClient,
                signerAddress ? (): void => undefined : setYaasStatus,
                address,
                data.account,
                shouldCommit
            );
            return data;
        } catch (error) {
            if (error instanceof Error) setError(error.message);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    return { requestChallenge, submitEnrollment, isLoading, error };
};
